import { memo } from 'react';
import { clsx } from 'clsx';
import { HStack, TextBlock } from '@library';
import type Category from '../../../../core/types/category';
import ChevronLeftIcon from '../../../../core/components/icons/ChevronLeftIcon';
import { ProgressSpinner } from '../../../../core/components/ProgressIndicators';
import sortEmptyCategories from '../../../../core/util/sortEmptyCategories';
import useTargetCategoryFiltersSearchParams from '../../../hooks/useTargetCategoryFiltersSearchParams';
import CustomLink from '../../../../core/components/CustomLink';
import styles from './CategoriesTree.module.scss';

interface CategoriesListProps {
    countsLoading?: boolean;
    items: Category[];
    showCount?: boolean;
    paramIgnoreList?: string[];
    activeCategoryId: string;
    categoryAncestryIds: string[];
}

const CategoryList = ({
    activeCategoryId,
    paramIgnoreList = [],
    countsLoading = false,
    showCount = true,
    items,
    categoryAncestryIds,
}: CategoriesListProps) => {
    items.sort(sortEmptyCategories);

    return (
        <>
            {items.map(item => (
                <CategoriesListItem
                    key={item.id}
                    paramIgnoreList={paramIgnoreList}
                    countsLoading={countsLoading}
                    showCount={showCount}
                    item={item}
                    activeCategoryId={activeCategoryId}
                    categoryAncestryIds={categoryAncestryIds}
                />
            ))}
        </>
    );
};

const useCategoriesListItem = ({
    activeCategoryId,
    paramIgnoreList,
    item,
}: {
    activeCategoryId: string;
    paramIgnoreList?: string[];
    item: Category;
}) => {
    const getSearchParams = useTargetCategoryFiltersSearchParams(item);

    const searchParams = getSearchParams(paramIgnoreList);

    const targetPath = `../categories/${item.id}?${searchParams.toString()}`;

    const { id, children } = item;

    const isLeafCategory =
        activeCategoryId === id ||
        children.some(
            child => child.id === activeCategoryId && !child.children.length
        );

    return {
        path: targetPath,
        isLeafCategory,
    };
};

const CategoriesListItem = memo(
    ({
        countsLoading = false,
        showCount = true,
        item,
        paramIgnoreList = [],
        activeCategoryId,
        categoryAncestryIds,
    }: {
        countsLoading?: boolean;
        showCount?: boolean;
        item: Category;
        paramIgnoreList?: string[];
        activeCategoryId: string;
        categoryAncestryIds: string[];
    }) => {
        const { id, name, children, parentId, count = 0 } = item;
        const { path, isLeafCategory } = useCategoriesListItem({
            activeCategoryId,
            paramIgnoreList,
            item,
        });

        const isParent = categoryAncestryIds.length > 1 && children.length > 0;
        const isActive = id === activeCategoryId;

        return (
            <div key={id} className={styles.categoryItemContainer}>
                <div className={clsx(styles.categoryItem)}>
                    <CustomLink
                        role="button"
                        tabIndex={0}
                        data-test-class="categoryMenuItem"
                        data-test-state={isParent ? 'parent' : 'child'}
                        className={clsx(
                            'category-link',
                            styles.categoryItemLink,
                            {
                                [styles.categoryItemLinkActive]: isActive,
                                [styles.disabled]: !count,
                            }
                        )}
                        to={path}
                    >
                        <div
                            className={clsx(styles.nav, styles.noMargin, {
                                [styles.parent]: parentId === null,
                                [styles.inactive]: showCount && !count,
                            })}
                        >
                            <span className={styles.backIcon}>
                                {isParent && !isActive && <ChevronLeftIcon />}
                            </span>

                            <HStack>
                                <TextBlock
                                    secondary
                                    truncate
                                    className={styles.itemName}
                                    medium={isActive}
                                    title={name}
                                >
                                    {name}
                                </TextBlock>
                                &nbsp;&nbsp;
                                {showCount && (
                                    <span className={styles.count}>
                                        {countsLoading ? (
                                            <ProgressSpinner />
                                        ) : (
                                            count
                                        )}
                                    </span>
                                )}
                            </HStack>
                        </div>
                    </CustomLink>
                </div>
                {!!children.length && categoryAncestryIds.includes(id) && (
                    <div
                        className={clsx({
                            [styles.leaf]: isLeafCategory,
                        })}
                    >
                        <CategoryList
                            paramIgnoreList={paramIgnoreList}
                            countsLoading={countsLoading}
                            items={children}
                            activeCategoryId={activeCategoryId}
                            categoryAncestryIds={categoryAncestryIds}
                        />
                    </div>
                )}
            </div>
        );
    }
);

export default memo(CategoryList);
